import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import {
  ModusButtonModule,
  ModusCheckboxModule,
  ModusIconModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  concatMap,
  debounce,
  forkJoin,
  map,
  of,
  pairwise,
  shareReplay,
  startWith,
  take,
  tap,
  timer,
} from 'rxjs';
import { ScandataFileService } from '../../../../scandata/scandata-file.service';
import { PointcloudAPIStatus, ScandataModel } from '../../../../scandata/scandata.models';
import { ScandataState } from '../../../../scandata/scandata.state';
import { SetView } from '../../../options-panel.actions';
import { OptionsPanelView } from '../../../options-panel.models';
import { DownloadProgressComponent } from '../../download-progress/download-progress.component';
import { AddToDownload } from '../../download.actions';
import { FileDownload, ScanDownload } from '../../download.models';
import { DownloadState } from '../../download.state';
import { downloadSelectListTranslations } from './download-select-list.translations';

interface DownloadAllSelect {
  visible: boolean;
  selected: boolean | 'indeterminate';
  disabled: boolean;
}

interface SelectableFile {
  scandataModelId: string;
  filename: string;
  selected: boolean;
}

interface ScanView extends ScanDownload {
  files: FileView[];
  selected: boolean | 'indeterminate';
  disabled: boolean;
}

interface FileView extends FileDownload {
  selected: boolean;
  disabled: boolean;
  downloadProgressTooltip?: string;
}

@UntilDestroy()
@Component({
  selector: 'sd-download-select-list',
  imports: [
    CommonModule,
    ModusButtonModule,
    ModusIconModule,
    ModusTooltipModule,
    ModusCheckboxModule,
    DownloadProgressComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './download-select-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DownloadSelectListComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly scandataFileService = inject(ScandataFileService);
  private readonly translationService = inject(TranslationService);

  readonly translations = downloadSelectListTranslations();

  readonly cancelClicked = output();
  readonly loading = output<boolean>();

  readonly pointcloudAPIStatus = PointcloudAPIStatus;

  readonly loading$ = new BehaviorSubject<boolean>(false);
  readonly selectableFiles$ = new BehaviorSubject<SelectableFile[]>([]);
  readonly selectedFiles$ = new BehaviorSubject<SelectableFile[]>([]);

  downloadAllSelect$!: Observable<DownloadAllSelect>;
  downloadDisabled$!: Observable<boolean>;
  selectedScansWithFiles$!: Observable<ScandataModel[]>;
  displayScans$!: Observable<ScanView[]>;

  ngOnInit() {
    this.selectedScansWithFiles$ = this.getSelectedScansWithFiles();
    this.subscribeToSelectedScansChange();

    this.displayScans$ = this.getDisplayScans();

    this.downloadAllSelect$ = combineLatest([this.displayScans$, this.loading$]).pipe(
      map(([scans, loading]) => {
        const visible = scans.length > 1;
        const files = scans.flatMap((scan) => scan.files);
        const disabled =
          files.length === 0 ? true : files.every((file) => isDefined(file.downloadProgress));

        const allFilesSelected =
          files.length > 0 && files.every((file) => file.disabled || file.selected);
        const noFilesSelected =
          files.length === 0 || files.every((file) => file.disabled || !file.selected);
        const selected = allFilesSelected ? true : noFilesSelected ? false : 'indeterminate';

        return {
          visible,
          disabled: loading || disabled,
          selected: disabled ? false : selected,
        } satisfies DownloadAllSelect;
      }),
    );

    this.downloadDisabled$ = combineLatest([
      this.displayScans$.pipe(startWith([])),
      this.loading$,
    ]).pipe(
      map(([scans, loading]) => {
        if (loading) return true;

        const files = scans.flatMap((scan) => scan.files);
        return isNil(files.find((file) => file.selected));
      }),
    );

    this.subscribeToSelectedFilesChange();
    this.subscribeToLoadingChange();
  }

  toggleExpanded(scanView: ScanView) {
    scanView.expanded = !scanView.expanded;
  }

  download() {
    this.displayScans$
      .pipe(
        take(1),
        map((scans) => {
          const actions = scans.flatMap((scan) => {
            return scan.files
              .filter((file) => file.selected)
              .map((file) => new AddToDownload(scan, file));
          });

          return this.store.dispatch(actions);
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.store.dispatch(new SetView(OptionsPanelView.DownloadStatus));
      });
  }

  selectAllClicked() {
    const selectableFiles = this.selectableFiles$.value;
    const allSelected = selectableFiles.every((file) => file.selected);
    const selected = allSelected ? false : true;

    selectableFiles.forEach((file) => (file.selected = selected));
    this.selectableFiles$.next(selectableFiles);
  }

  selectScanClicked(scan: ScanView) {
    const selectableFiles = this.selectableFiles$.value;
    const scanFiles = selectableFiles.filter(
      (selectableFile) => selectableFile.scandataModelId === scan.scandataModel.id,
    );

    const allSelected = scanFiles.every((file) => file.selected);
    const selected = allSelected ? false : true;

    scanFiles.forEach((file) => (file.selected = selected));
    this.selectableFiles$.next(selectableFiles);
  }

  selectFileClicked($event: MouseEvent, file: FileView) {
    if ($event.shiftKey) return this.toggleRangeSelected(file);

    this.toggleSingleSelected(file);
  }

  private toggleRangeSelected(file: FileView) {
    const selectedFiles = this.selectedFiles$.value;
    if (selectedFiles.length === 0) return;

    const selectableFiles = this.selectableFiles$.value;
    const lastSelectedFile = selectedFiles.slice(-1)[0];
    const lastSelectedIndex = selectableFiles.findIndex((selectableFile, index) => {
      return selectableFile.scandataModelId === lastSelectedFile.scandataModelId &&
        selectableFile.filename === lastSelectedFile.filename
        ? index
        : null;
    });

    const selectedIndex = selectableFiles.findIndex((selectableFile, index) => {
      return selectableFile.scandataModelId === file.scandataModelId &&
        selectableFile.filename === file.scandataFile.name
        ? index
        : null;
    });

    const startIndex = selectedIndex < lastSelectedIndex ? selectedIndex : lastSelectedIndex;
    const endIndex = selectedIndex > lastSelectedIndex ? selectedIndex : lastSelectedIndex;
    const selection = selectableFiles.filter(
      (_, index) => index >= startIndex && index <= endIndex,
    );
    if (selectedIndex < lastSelectedIndex) selection.reverse();

    const select = !file.selected;
    selection.forEach((scan) => {
      scan.selected = select;
      this.selectableFiles$.next(selectableFiles);
    });
  }

  private toggleSingleSelected(file: FileView) {
    const selectableFiles = this.selectableFiles$.value;
    const selectedFile = selectableFiles.find(
      (selectableFile) =>
        selectableFile.scandataModelId === file.scandataModelId &&
        selectableFile.filename === file.scandataFile.name,
    );

    if (isNil(selectedFile)) return;

    selectedFile.selected = !selectedFile.selected;
    this.selectableFiles$.next(selectableFiles);
  }

  private subscribeToSelectedFilesChange() {
    this.selectableFiles$.pipe(untilDestroyed(this)).subscribe((selectableFiles) => {
      const selectedFiles = this.selectedFiles$.value;

      // filter out no longer selected
      const keepSelection = selectedFiles.filter((selected) =>
        isDefined(
          selectableFiles.find(
            (file) =>
              file.scandataModelId === selected.scandataModelId &&
              file.filename === selected.filename &&
              file.selected,
          ),
        ),
      );

      // add new selected
      const addSelection = selectableFiles.filter((file) => {
        if (!file.selected) return false;

        return isNil(
          keepSelection.find((selected) => {
            return (
              selected.scandataModelId === file.scandataModelId &&
              selected.filename === file.filename
            );
          }),
        );
      });

      this.selectedFiles$.next([...keepSelection, ...addSelection]);
    });
  }

  private subscribeToSelectedScansChange() {
    this.selectedScansWithFiles$.pipe(untilDestroyed(this)).subscribe((scans) => {
      const selectableFiles = this.selectableFiles$.value;
      const newSelectableFiles: SelectableFile[] = scans
        .flatMap((scan) =>
          scan.files?.map((file) => {
            return (
              selectableFiles.find((selectableFile) => {
                return (
                  selectableFile.scandataModelId === scan.id &&
                  selectableFile.filename === file.name
                );
              }) ?? {
                scandataModelId: scan.id,
                filename: file.name,
                selected: true,
              }
            );
          }),
        )
        .filter(isDefined);

      this.selectableFiles$.next(newSelectableFiles);
    });
  }

  private subscribeToLoadingChange() {
    this.loading$.pipe(untilDestroyed(this)).subscribe((loading) => this.loading.emit(loading));
  }

  private getIcon(filename: string) {
    const extension = filename.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'image';
      default:
        return 'file';
    }
  }

  private getDisplayScans(): Observable<ScanView[]> {
    const selectableFiles$ = this.selectableFiles$.pipe(debounce(() => timer(100)));

    const scans$ = combineLatest([this.getScanDownloads(), selectableFiles$, this.loading$]).pipe(
      map(([scans, selectableFiles, loading]) => {
        return scans.map((scan) => {
          const files: FileView[] = scan.files
            .map((file) => {
              const downloading = isDefined(file.downloadProgress);
              const selected = downloading
                ? false
                : (selectableFiles.find(
                    (selectableFile) =>
                      selectableFile.scandataModelId === scan.scandataModel.id &&
                      selectableFile.filename === file.scandataFile.name,
                  )?.selected ?? true);

              return {
                ...file,
                selected,
                disabled: loading || downloading,
                icon: this.getIcon(file.scandataFile.name),
                downloadProgressTooltip: downloading
                  ? this.translationService.translate(this.translations.fileProgress.tooltip.text, {
                      percentageComplete: file.downloadProgress!.percentageComplete,
                    })
                  : undefined,
              };
            })
            .filter(isDefined);

          const selected =
            files.length === 0 || files.every((file) => !file.selected)
              ? false
              : files.every((file) => file.selected || file.disabled)
                ? true
                : 'indeterminate';

          const scanView: ScanView = {
            ...scan,
            files,
            selected,
            disabled:
              loading ||
              files.length === 0 ||
              files.every((file) => isDefined(file.downloadProgress)),
          };

          return scanView;
        });
      }),
    );

    // preserve expand/collapse state
    return scans$.pipe(
      startWith(new Array<ScanView>()),
      pairwise(),
      map(([prev, current]) => {
        current.forEach((scanView) => {
          scanView.expanded =
            prev.find((prevScanView) => prevScanView.scandataModel.id === scanView.scandataModel.id)
              ?.expanded ?? scanView.expanded;
        });

        return current;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private getSelectedScansWithFiles() {
    return this.store.select(ScandataState.chronologicalSelected).pipe(
      // debounce because select all from the scandata list emits multiple times
      debounce(() => timer(100)),
      concatMap(() => {
        this.loading$.next(true);

        // Because concatMap waits for the observable to complete and projects each source,
        // the source may be stale by the time it get's here.
        // To avoid the stale scans I select them from the store again.
        const loadScans$ = this.store
          .selectSnapshot(ScandataState.chronologicalSelected)
          .map((scan) => {
            if (isDefined(scan.files)) return of(scan);
            if (scan.status !== PointcloudAPIStatus.Ready) return of(scan);

            return this.scandataFileService.loadScandataFiles(scan);
          });

        return forkJoin(loadScans$);
      }),
      tap(() => this.loading$.next(false)),
      map((scans) => {
        // sort files
        scans.forEach((scan) => {
          scan.files?.sort((a, b) => a.name.localeCompare(b.name));
        });

        return scans;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private getScanDownloads(): Observable<ScanDownload[]> {
    return combineLatest([
      this.selectedScansWithFiles$,
      this.store.select(DownloadState.downloads),
    ]).pipe(
      map(([scans, downloads]) => {
        return scans.map((scan) => {
          const scanDownload = downloads.find((download) => download.scandataModel.id === scan.id);
          const files = this.getFileDownloads(scan, scanDownload);
          const scanDownloadView: ScanDownload = {
            scandataModel: scan,
            files,
            expanded: scanDownload?.expanded ?? true,
          };

          return scanDownloadView;
        });
      }),
    );
  }

  private getFileDownloads(scan: ScandataModel, scanDownload?: ScanDownload): FileDownload[] {
    return (
      scan.files?.map((scandataFile) => {
        return (
          scanDownload?.files.find(
            (fileDownload) => fileDownload.scandataFile.name === scandataFile.name,
          ) ?? {
            scandataModelId: scan.id,
            scandataFile,
            icon: this.getIcon(scandataFile.name),
          }
        );
      }) ?? []
    );
  }
}
